import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { storesApi } from '../api/stores';
import { toErrorMessage } from '../api/client';
import { Card, PageHead, ActiveBadge } from '../components/ui';
import { Empty, ErrorState, Loading } from '../components/DataStates';
import { Modal } from '../components/Modal';
import type { Store, StoreCreateRequest } from '../types';

type Draft = StoreCreateRequest & { active?: boolean };

const EMPTY: Draft = {
  code: '',
  name: '',
  address: '',
  phone: '',
  gstin: '',
  billPrefix: '',
};

export function StoresPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Store | null>(null);
  const [creating, setCreating] = useState(false);

  const q = useQuery({ queryKey: ['stores'], queryFn: storesApi.list });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['stores'] });

  return (
    <>
      <PageHead
        title="Stores"
        subtitle="Manage the restaurant locations in the group."
        actions={
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            + New Store
          </button>
        }
      />

      <Card>
        {q.isLoading ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState message={toErrorMessage(q.error)} onRetry={q.refetch} />
        ) : !q.data || q.data.length === 0 ? (
          <Empty
            icon="🏬"
            title="No stores yet"
            hint="Create your first location to get started."
          />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>GSTIN</th>
                  <th>Bill Prefix</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <strong>{s.code}</strong>
                    </td>
                    <td>{s.name}</td>
                    <td>{s.phone || '—'}</td>
                    <td>{s.gstin || '—'}</td>
                    <td>{s.billPrefix || '—'}</td>
                    <td>
                      <ActiveBadge active={s.active} />
                    </td>
                    <td className="text-right">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setEditing(s)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && (
        <StoreFormModal
          title="New Store"
          initial={EMPTY}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            invalidate();
          }}
          submit={(draft) => storesApi.create(draft)}
        />
      )}

      {editing && (
        <StoreFormModal
          title={`Edit ${editing.name}`}
          isEdit
          initial={{
            code: editing.code,
            name: editing.name,
            address: editing.address ?? '',
            phone: editing.phone ?? '',
            gstin: editing.gstin ?? '',
            billPrefix: editing.billPrefix ?? '',
            active: editing.active,
          }}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
          submit={(draft) => storesApi.update(editing.id, draft)}
        />
      )}
    </>
  );
}

function StoreFormModal({
  title,
  initial,
  isEdit,
  onClose,
  onSaved,
  submit,
}: {
  title: string;
  initial: Draft;
  isEdit?: boolean;
  onClose: () => void;
  onSaved: () => void;
  submit: (draft: Draft) => Promise<Store>;
}) {
  const [draft, setDraft] = useState<Draft>(initial);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: submit,
    onSuccess: onSaved,
    onError: (err) => setError(toErrorMessage(err)),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate(draft);
  }

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));

  return (
    <Modal
      open
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={mutation.isPending || !draft.code || !draft.name}
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form onSubmit={onSubmit}>
        {error && <div className="form-error">{error}</div>}
        <div className="field-row">
          <div className="field">
            <label>Store Code *</label>
            <input
              value={draft.code}
              disabled={isEdit}
              onChange={(e) => set({ code: e.target.value.toUpperCase() })}
              placeholder="GCB"
              required
            />
            {isEdit && <div className="hint">Code cannot be changed.</div>}
          </div>
          <div className="field">
            <label>Name *</label>
            <input
              value={draft.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="Gachibowli"
              required
            />
          </div>
        </div>
        <div className="field">
          <label>Address</label>
          <textarea
            value={draft.address ?? ''}
            onChange={(e) => set({ address: e.target.value })}
          />
        </div>
        <div className="field-row">
          <div className="field">
            <label>Phone</label>
            <input
              value={draft.phone ?? ''}
              onChange={(e) => set({ phone: e.target.value })}
            />
          </div>
          <div className="field">
            <label>GSTIN</label>
            <input
              value={draft.gstin ?? ''}
              onChange={(e) => set({ gstin: e.target.value })}
            />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label>Bill Prefix</label>
            <input
              value={draft.billPrefix ?? ''}
              onChange={(e) => set({ billPrefix: e.target.value })}
              placeholder="GCB"
            />
          </div>
          {isEdit && (
            <div className="field">
              <label>Status</label>
              <select
                value={draft.active ? 'true' : 'false'}
                onChange={(e) => set({ active: e.target.value === 'true' })}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}
